import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import DetailsListAbstract, {
    ICoreColumn,
    ICoreDetailsListFilter,
    ICoreDetailsListSettings,
} from '../../DetailsList/DetailsListAbstract'
import { TaskAction, TaskOverviewDto, TaskStatus } from '../../../config/taskConfig'
import {
    Checkbox,
    getTheme,
    Icon,
    IContextualMenuItem,
    mergeStyles,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text,
} from '@fluentui/react'
import { Link, useHistory } from 'react-router-dom'
import {
    includeColumnFilter,
    multiSelectFilter,
    personaColumn,
    shortDateColumn,
} from '../../../helpers/detailsListHelper'
import useUser from '../../../hooks/useUser'
import { useDispatch } from 'react-redux'
import { DocumentOverviewItemDto } from '../../../config/documentConfig'
import { UserDto } from '../../../config/userConfig'
import { Dispatch } from 'redux'
import { regexSearchable } from '../../../helpers/regexHelper'
import { taskBaseUrl } from '../../../pages/Tasks'
import DocumentName from '../../Render/DocumentName'
import { AxiosInstance } from 'axios'
import useAxios from '../../../hooks/useAxios'
import useEntity from '../../../hooks/useEntity'
import { Destination, GraphError } from '../../../context/AxiosContext'
import { driveItemOverviewProperties, mapDriveItemToOverviewItemDto } from '../../../helpers/documentHelper'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import IconValue from '../../IconValue/IconValue'
import { AxiosPromiseAction } from '../../../reducers'
import { useTranslation } from 'react-i18next'

export type TaskListSettings = ICoreDetailsListSettings & {
    from?: string
    to?: string
    assignees?: string[]
    assigners?: string[]
    statuses?: TaskStatus[]
    excludeOwnerIsAssignee?: boolean
    excludeOwnerIsAssigner?: boolean
}

export interface TaskListProps {
    data: TaskOverviewDto[]
    reset: () => any // Callback when a task is toggled.
    baseUrl: string
    global?: boolean // If the file column should be shown.
    filters?: ICoreDetailsListFilter<TaskOverviewDto, TaskListSettings>[]
    settings?: Partial<TaskListSettings>
}

const getFilters = (dispatch: Dispatch, currentUser: UserDto) => [
    multiSelectFilter(
        (settings: TaskListSettings) => settings.assignees,
        (item: TaskOverviewDto) => item.assignee && item.assignee.id,
    ),
    multiSelectFilter(
        (settings: TaskListSettings) => settings.assigners,
        (item: TaskOverviewDto) => item.assigner && item.assigner.id,
    ),
    (items: TaskOverviewDto[], settings: TaskListSettings) =>
        currentUser
            ? items.filter(
                  (item: TaskOverviewDto) =>
                      item.assignee &&
                      item.assigner &&
                      !(settings.excludeOwnerIsAssignee && currentUser.id === item.assignee.id) &&
                      !(settings.excludeOwnerIsAssigner && currentUser.id === item.assigner.id),
              )
            : items,
    (items: TaskOverviewDto[], settings: TaskListSettings) =>
        settings.statuses && settings.statuses.length > 0
            ? items.filter((item: TaskOverviewDto) => settings.statuses.includes(item.status))
            : items,
]

const getColumns = (
    dispatch: Dispatch,
    axios: AxiosInstance,
    translate: (key: string) => string,
    props: TaskListProps,
): ICoreColumn[] => [
    {
        key: 'toggle',
        name: translate('base.table.header.status'),
        minWidth: 24,
        maxWidth: 24,
        isResizable: false,
        iconName: 'CheckMark',
        headerClassName: 'align-center',
        onRender: (item: TaskOverviewDto) => (
            <Checkbox
                checked={item.status !== TaskStatus.Open}
                onChange={async () => {
                    await (dispatch({
                        type: TaskAction.Toggle,
                        payload: axios.post(`/task/${item.id}/toggle-status`),
                    }) as AxiosPromiseAction)
                    await props.reset()
                }}
            />
        ),
    },
    personaColumn({ key: 'assignee', name: translate('base.table.header.assignee') }),
    {
        key: 'description',
        name: translate('base.table.header.description'),
        fieldName: 'description',
        minWidth: 160,
        isResizable: true,
        isRowHeader: true,
        isSortable: true,
        isMultiline: true,
        data: (item: TaskOverviewDto) => regexSearchable(item.description),
        onRender: (item: TaskOverviewDto) => (
            <>
                {props.global && item.file && (
                    <Text nowrap block variant="small">
                        <Link to={`/file/${item.file.id}${taskBaseUrl}`}>
                            {item.file.reference} - {item.file.name}
                        </Link>
                    </Text>
                )}
                <Text variant="medium">{item.description || <em>Omschrijving ontbreekt.</em>}</Text>
                <Attachments ids={item.attachmentIds || []} />
            </>
        ),
    },
    shortDateColumn({ key: 'due', isSortedDescending: false, isSorted: true, isResizable: true }),
    personaColumn({
        key: 'assigner',
        name: translate('base.table.header.assigner'),
        iconName: 'assign',
        isResizable: true,
    }),
]

function Attachments({ ids }: { ids: string[] }): ReactElement {
    if (ids.length === 0) {
        return <></>
    }

    return (
        <Stack horizontal tokens={{ childrenGap: 's1' }}>
            <StackItem>
                <Icon iconName="Attach" />
            </StackItem>
            <Stack>
                {ids.map((id: string) => (
                    <StackItem key={id}>
                        <Attachment id={id} />
                    </StackItem>
                ))}
            </Stack>
        </Stack>
    )
}

function Attachment({ id }: { id: string }): ReactElement {
    const { t: translate } = useTranslation()

    const [attachment] = useEntity<DocumentOverviewItemDto, DriveItem, GraphError>(
        id => ({
            url: `/items/${id}`,
            method: 'GET',
            baseURL: Destination.Documents,
            params: { $select: driveItemOverviewProperties.join(',') },
        }),
        id,
        response => mapDriveItemToOverviewItemDto(response.data),
    )

    if (attachment.rejected) {
        return (
            <IconValue
                iconName="Warning"
                value={translate('base.empty')}
                wrapperClassName={mergeStyles({ color: getTheme().semanticColors.warningIcon })}
            />
        )
    }

    if (attachment.loading || !attachment.loaded) {
        return <Spinner label={translate('base.loading')} size={SpinnerSize.xSmall} labelPosition="right" />
    }

    return (
        <DocumentName
            id={attachment.data.id}
            isLink={true}
            name={attachment.data.name}
            url={attachment.data.url}
            nodeType={attachment.data.type}
        />
    )
}

export default function TaskList(props: TaskListProps): ReactElement {
    const axios = useAxios()
    const dispatch = useDispatch()
    const history = useHistory()
    const currentUser = useUser()
    const { t: translate } = useTranslation()

    const columns = useMemo<ICoreColumn[]>(
        () =>
            getColumns(dispatch, axios, translate, props).filter(column =>
                includeColumnFilter(column, ['file'], props.global),
            ),
        [props],
    )

    const menuItem = useMemo<IContextualMenuItem>(
        () => ({
            key: 'edit',
            iconProps: { iconName: 'Edit' },
            text: translate('action.edit'),
            click: (item: TaskOverviewDto) => history.push(`${props.baseUrl}/${item.id}/edit`),
        }),
        [props.baseUrl],
    )

    const filters = useMemo<ICoreDetailsListFilter<TaskOverviewDto, TaskListSettings>[]>(
        () => [...getFilters(dispatch, currentUser), ...(props.filters || [])],
        [currentUser, props.filters],
    )

    return (
        <DetailsListAbstract
            columns={columns}
            items={props.data}
            filters={filters}
            settings={props.settings}
            isSortable
            contextualMenuItems={[[menuItem]]}
            getContextualMenuTitle={(item: TaskOverviewDto) =>
                item.description.length > 30 ? item.description.substring(0, 24).trim() + '\u2026' : item.description
            }
            emptyDescription={translate('base.table.empty')}
        />
    )
}
